import { BookOpen } from "lucide-react";

// Safe Markdown-lite renderer: builds React elements only (no innerHTML), so no HTML/script injection is possible.
// Supports: #/##/### headings, **bold**, "- " / "* " bullets, "1. " numbered lists, paragraphs, and [DOC_ID:x] citation badges.
function inline(text, sources, onCitationClick, keyBase) {
  const out = [];
  const re = /(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(\[DOC_ID:([a-z0-9\-]+)\])/gi;
  let last = 0; let m; let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(<span key={`${keyBase}-t${k++}`}>{text.slice(last, m.index)}</span>);
    if (m[1]) out.push(<strong key={`${keyBase}-b${k++}`}>{m[1].slice(2, -2)}</strong>);
    else if (m[2]) out.push(<em key={`${keyBase}-i${k++}`}>{m[2].slice(1, -1)}</em>);
    else {
      const id = m[4];
      const src = sources.find((s) => s.doc_id.toLowerCase() === id.toLowerCase());
      out.push(
        <button key={`${keyBase}-c${k++}`} type="button" data-testid="citation-badge" className="citation-badge" onClick={(e) => { e.stopPropagation(); onCitationClick(id); }}>
          <BookOpen className="w-3 h-3" /> {src ? src.citation : "Source"}
        </button>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(<span key={`${keyBase}-t${k++}`}>{text.slice(last)}</span>);
  return out;
}

export default function ReplyMarkdown({ text, sources = [], onCitationClick }) {
  const lines = (text || "").replace(/\r/g, "").split("\n");
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
    const ol = /^\s*(\d+)[.)]\s+(.*)$/.exec(line);
    if (h) { flush(); blocks.push({ type: `h${h[1].length}`, text: h[2], i }); }
    else if (ul || ol) {
      const type = ul ? "ul" : "ol";
      if (!list || list.type !== type) { flush(); list = { type, items: [], i }; }
      list.items.push((ul || ol)[ul ? 1 : 2]);
    } else if (!line.trim()) flush();
    else { flush(); blocks.push({ type: "p", text: line, i }); }
  });
  flush();
  return (
    <div className="reply-md space-y-2">
      {blocks.map((b) => {
        if (b.type === "ul" || b.type === "ol") {
          const Tag = b.type;
          return <Tag key={b.i} className={`${b.type === "ul" ? "list-disc" : "list-decimal"} pl-5 space-y-1`}>{b.items.map((it, j) => <li key={j}>{inline(it, sources, onCitationClick, `${b.i}-${j}`)}</li>)}</Tag>;
        }
        if (b.type === "h1" || b.type === "h2") return <h3 key={b.i} className="font-display font-bold text-[#0A2A5E] text-base mt-3">{inline(b.text, sources, onCitationClick, b.i)}</h3>;
        if (b.type === "h3") return <h4 key={b.i} className="font-bold text-[#0A2A5E] text-sm mt-2">{inline(b.text, sources, onCitationClick, b.i)}</h4>;
        return <p key={b.i}>{inline(b.text, sources, onCitationClick, b.i)}</p>;
      })}
    </div>
  );
}
